import { HttpContext, httpResource, HttpResourceRef } from '@angular/common/http';
import { computed, Service, Signal } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { HANDLE_HTTP_ERROR_LOCALLY } from '../../../core/http/http-context-tokens';
import {
  CategoriaNoticiaResponse,
  Noticia,
  NOTICIA_FILTRO_TODOS,
  NoticiaCategoria,
  NoticiaDetalhe,
  NoticiaDetalhePublicaResponse,
  NoticiaPaginaPublicaResponse,
  NoticiaPublicaResponse,
} from '../models/noticia.model';

export const RECURSO_NOTICIA = `${environment.apiUrl}/sai/cartaservico/publico/noticia`;

/** O que a homepage carrega de uma vez: os chips e a busca filtram no cliente. */
export const TAMANHO_HOMEPAGE = 24;

/** O erro fica local: sem notícia, a tela mostra o estado vazio, sem modal. */
const ERRO_LOCAL = () => new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true);

export interface ParametrosListagemNoticia {
  categoria?: number | null;
  busca?: string | null;
  pagina?: number;
  tamanho?: number;
}

export interface ListaNoticias {
  readonly noticias: Signal<readonly Noticia[]>;
  readonly carregando: Signal<boolean>;
}

export interface PaginaNoticias extends ListaNoticias {
  readonly total: Signal<number>;
}

export interface DetalheNoticia {
  readonly noticia: Signal<NoticiaDetalhe | null>;
  readonly carregando: Signal<boolean>;
}

export interface CategoriasNoticia {
  readonly categorias: Signal<readonly NoticiaCategoria[]>;
  readonly carregando: Signal<boolean>;
}

export function paraCategoria(categoria: CategoriaNoticiaResponse): NoticiaCategoria {
  return {
    id: String(categoria.codCategoriaServicoCtg),
    rotulo: categoria.desNomeCtg,
    cor: categoria.desCorTemaCtg || NOTICIA_FILTRO_TODOS.cor,
  };
}

/** A URL pública da arte. A mobile só é pedida quando existe: a API responde 404 sem cair na desktop. */
export function urlImagemNoticia(id: number, mobile = false): string {
  return `${RECURSO_NOTICIA}/${id}/imagem${mobile ? '?formato=mobile' : ''}`;
}

export function paraNoticia(item: NoticiaPublicaResponse): Noticia {
  return {
    id: item.codNoticiaNtc,
    slug: item.desSlugNtc,
    titulo: item.desTituloNtc,
    descricao: item.desSubtituloNtc,
    data: item.datPublicacaoNtc,
    categoria: item.categoria ? paraCategoria(item.categoria) : null,
    imagem: urlImagemNoticia(item.codNoticiaNtc),
    imagemMobile: item.bitPossuiImagemMobile ? urlImagemNoticia(item.codNoticiaNtc, true) : null,
  };
}

export function paraDetalhe(item: NoticiaDetalhePublicaResponse): NoticiaDetalhe {
  const desktop = item.imagens.find((imagem) => imagem.desFormatoNim === 'Desktop');
  const mobile = item.imagens.find((imagem) => imagem.desFormatoNim === 'Mobile');

  return {
    ...paraNoticia(item),
    autor: item.desAutorNtc?.trim() || null,
    departamento: item.desNomeDepartamento,
    textoAlternativo: desktop?.desTextoAlternativoNim ?? '',
    textoAlternativoMobile: mobile?.desTextoAlternativoNim ?? null,
    legenda: desktop?.desLegendaNim ?? null,
    credito: desktop?.desCreditoNim ?? null,
    textoPrincipal: item.desTextoPrincipalNtc ?? '',
    citacao: item.desCitacaoNtc,
    secoes: item.secoes,
    card: item.cardApoio,
    botao: item.botaoServico,
    compartilhamento: item.bitExibirCompartilhamentoNtc,
    redes: item.redes.map((rede) => ({ nome: rede.desNomeNrd, url: rede.desUrlNrd })),
  };
}

/** Só os parâmetros informados entram na query: o servidor aplica os padrões. */
export function parametrosDaListagem(parametros: ParametrosListagemNoticia): Record<string, string> {
  const params: Record<string, string> = {};
  const busca = parametros.busca?.trim();

  if (parametros.categoria) params['categoria'] = String(parametros.categoria);
  if (busca) params['busca'] = busca;
  if (parametros.pagina) params['pagina'] = String(parametros.pagina);
  if (parametros.tamanho) params['tamanho'] = String(parametros.tamanho);

  return params;
}

/** O valor do recurso, ou nulo em erro: `value()` lança quando o recurso está em erro. */
function valor<T>(recurso: HttpResourceRef<ResponseApi<T> | undefined>): T | null {
  if (recurso.error() !== undefined) return null;

  return recurso.value()?.data ?? null;
}

/**
 * As notícias publicáveis do portal, lidas das rotas públicas. O portal não envia o órgão:
 * o back-end o resolve pelo host.
 *
 * Cada método cria um `httpResource` e precisa ser chamado num contexto de injeção (o
 * inicializador de campo do componente): o recurso pertence à tela e só busca quando ela abre.
 * Em falha, cada recurso devolve vazio (ou nulo, no detalhe).
 */
@Service()
export class NoticiasService {
  /** As 24 mais recentes, para a seção da homepage. */
  homepage(): ListaNoticias {
    return this.lista(() => ({ tamanho: TAMANHO_HOMEPAGE }));
  }

  /**
   * Uma página de `/noticias`, refeita quando os parâmetros mudam. Parâmetros nulos não
   * buscam nada (ex.: as relacionadas antes de a notícia aberta chegar).
   */
  listagem(parametros: () => ParametrosListagemNoticia | null): PaginaNoticias {
    const recurso = httpResource<ResponseApi<NoticiaPaginaPublicaResponse>>(() => {
      const atuais = parametros();

      return atuais
        ? { url: RECURSO_NOTICIA, params: parametrosDaListagem(atuais), context: ERRO_LOCAL() }
        : undefined;
    });

    return {
      noticias: computed(() => (valor(recurso)?.itens ?? []).map(paraNoticia)),
      total: computed(() => valor(recurso)?.numTotal ?? 0),
      carregando: computed(() => recurso.isLoading()),
    };
  }

  /** Uma lista sem paginação na tela (homepage, relacionadas, últimas). */
  lista(parametros: () => ParametrosListagemNoticia | null): ListaNoticias {
    const { noticias, carregando } = this.listagem(parametros);

    return { noticias, carregando };
  }

  /** Até 4 notícias em destaque. */
  destaques(): ListaNoticias {
    const recurso = httpResource<ResponseApi<NoticiaPublicaResponse[]>>(() => ({
      url: `${RECURSO_NOTICIA}/destaque`,
      context: ERRO_LOCAL(),
    }));

    return {
      noticias: computed(() => (valor(recurso) ?? []).map(paraNoticia)),
      carregando: computed(() => recurso.isLoading()),
    };
  }

  /** As categorias com notícia publicável, na ordem editorial: os chips de `/noticias`. */
  categorias(): CategoriasNoticia {
    const recurso = httpResource<ResponseApi<CategoriaNoticiaResponse[]>>(() => ({
      url: `${RECURSO_NOTICIA}/categoria`,
      context: ERRO_LOCAL(),
    }));

    return {
      categorias: computed(() => (valor(recurso) ?? []).map(paraCategoria)),
      carregando: computed(() => recurso.isLoading()),
    };
  }

  /** O detalhe pelo slug. Nulo para inexistente, não publicável ou falha. */
  detalhe(slug: () => string): DetalheNoticia {
    const recurso = httpResource<ResponseApi<NoticiaDetalhePublicaResponse>>(() =>
      slug()
        ? { url: `${RECURSO_NOTICIA}/${encodeURIComponent(slug())}`, context: ERRO_LOCAL() }
        : undefined,
    );

    return {
      noticia: computed(() => {
        const dados = valor(recurso);
        return dados ? paraDetalhe(dados) : null;
      }),
      carregando: computed(() => recurso.isLoading()),
    };
  }

  rota(noticia: Noticia): readonly string[] {
    return ['/noticias', noticia.slug];
  }
}
